
import React, { useEffect } from "react";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useEvangelismAssistant } from "./EvangelismAssistantProvider";
import { toast } from "@/hooks/use-toast";

export const ConversionTracker: React.FC = () => {
  const { conversionStage, conversionProgress, softeningScore } = useEvangelismAssistant();

  // Effect to celebrate conversion when it happens
  useEffect(() => {
    if (conversionStage === "converted") {
      // Celebrate conversion with a toast notification
      toast({
        title: "Praise God!",
        description: "This soul has accepted Christ!",
        variant: "default",
      });
    }
  }, [conversionStage]);

  const getStageEmoji = (stage: string): string => {
    switch (stage) {
      case "hardened": return "👎";
      case "skeptical": return "😐";
      case "curious": return "🤔";
      case "convicted": return "🙏";
      case "ready": return "❤️";
      case "converted": return "✅";
      default: return "";
    }
  };

  const stageLabels = [
    { id: "hardened", label: "Hardened" },
    { id: "skeptical", label: "Skeptical" },
    { id: "curious", label: "Curious" },
    { id: "convicted", label: "Convicted" },
    { id: "ready", label: "Ready to Believe" },
    { id: "converted", label: "Born Again" },
  ];

  // Calculate the color for the softening score based on the value
  const getSofteningScoreColor = () => {
    if (softeningScore >= 17) return "text-green-600 font-semibold";
    if (softeningScore >= 13) return "text-teal-600";
    if (softeningScore >= 9) return "text-blue-600";
    if (softeningScore >= 5) return "text-amber-600";
    return "text-gray-500";
  };
  
  return (
    <Card className={`border-heaven-gold/20 ${conversionStage === "converted" ? "bg-heaven-gold/10" : ""}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-md font-semibold text-[#403E43] flex justify-between items-center">
          <span>Spiritual Journey</span>
          <span className={`text-sm font-normal ${getSofteningScoreColor()}`}>
            Score: {softeningScore}/20
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Progress 
          value={conversionProgress} 
          className={`h-2 mb-4 ${conversionStage === "converted" ? "bg-heaven-gold/20" : ""}`} 
        />
        <div className="space-y-2 mt-2">
          {stageLabels.map((stage) => (
            <div 
              key={stage.id} 
              className={`flex items-center p-2 rounded transition-all duration-300 ${
                conversionStage === stage.id 
                  ? 'bg-heaven-gold/10 font-medium' 
                  : conversionStage === "converted" && stage.id === "converted"
                  ? 'bg-green-100 text-green-800 font-semibold'
                  : 'text-gray-500'
              }`}
            >
              <span className="mr-2">{getStageEmoji(stage.id)}</span>
              <span className="text-sm">{stage.label}</span>
              {conversionStage === stage.id && stage.id === "converted" && (
                <span className="ml-auto text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full">
                  Saved!
                </span>
              )}
            </div>
          ))}
        </div>
        
        {/* Debug score display (optional) */}
        <p className="text-xs text-gray-400 mt-4">Debug: score={softeningScore}, stage={conversionStage}</p>
      </CardContent>
    </Card>
  );
};
